/**
 * Centralized asset helpers.
 *
 * Every `<img>` / `next/image` reference in the landing codebase should route
 * through one of these helpers so that the on-disk public folder layout can
 * evolve without ripping through dozens of components.
 *
 *   asset("icons/ui/check.svg")  → "/assets/icons/ui/check.svg"
 *   figmaAsset("973:41088")      → "/assets/figma/app-home-973-41088.png"
 *   figmaAsset("973:41088","2x") → "/assets/figma/app-home-973-41088@2x.png"
 */

/** Prefix map for Figma node renders, keyed by nodeId. */
const FIGMA_NODE_PREFIX: Record<string, string> = {
  "973:41088": "app-home-973-41088",
  "604:37869": "feature-604-37869",
  "602:37746": "feature-602-37746",
};

/**
 * Resolve an asset under `public/assets/**`.
 *
 * Accepts paths with or without a leading slash, with or without the
 * `assets/` prefix, and always returns a rooted URL at `/assets/<path>`.
 */
export function asset(path: string): string {
  const trimmed = path.replace(/^\/+/, "").replace(/^assets\//, "");
  return `/assets/${trimmed}`;
}

/** Back-compat alias — older call sites imported `assetUrl`. */
export const assetUrl = asset;

/**
 * Resolve a Figma-sourced reference render under `public/assets/figma/`.
 *
 * @param nodeId  Figma node ID, e.g. "973:41088". Must be registered in
 *                `FIGMA_NODE_PREFIX`; unknown IDs fall back to the raw ID.
 * @param variant "1x" (default) or "2x".
 */
export function figmaAsset(nodeId: string, variant: "1x" | "2x" = "1x"): string {
  const prefix = FIGMA_NODE_PREFIX[nodeId] ?? nodeId.replace(":", "-");
  const suffix = variant === "2x" ? "@2x" : "";
  return `/assets/figma/${prefix}${suffix}.png`;
}

/** Logo sources (kept here so callers don't hardcode brand paths). */
export const brandLogo = {
  iconLight: asset("brand/icon_outline_light.svg"),
  iconDark: asset("brand/icon_outline_dark.svg"),
  textLight: asset("brand/text_only_light.svg"),
  textDark: asset("brand/text_only_dark.svg"),
  horizontal: asset("brand/horizontal_outline.svg"),
  stacked: asset("brand/stacked_outline.svg"),
  layout01Dark: asset("brand/rr-layout-01-dark.svg"),
  layout01Light: asset("brand/rr-layout-01-white.svg"),
  layout02Light: asset("brand/rr-layout-02-white.svg"),
  /**
   * 512×512 PNG of the RR sign negative, on white.
   * Organization JSON-LD and SoftwareApplication schema use this URL.
   */
  markPng: asset("brand/rr-logo.png"),
  /**
   * Cropped RR sign (black disc, mark knocked out). Byte-identical to
   * `ads-material/brand/rr_sign_negative.svg`. The uncropped
   * `rr-sign-negative.svg` is an A4 artboard and must not be used as an icon.
   */
  signNegative: asset("brand/rr-sign-negative-black.svg"),
} as const;
